import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Input,
  message,
  Segmented,
  Spin,
  Table,
  Tag,
} from 'antd';
import { API } from '@/services/api.service';
import {
  firstPartParams,
  firstPartQueryOptions,
} from './firstPartQuery';

const ACTION_COLOR = {
  CONSECUTIVE: 'gold',
  USE_ALL: 'volcano',
  NO_DAYS_LEFT: 'default',
  MATERNITY: 'purple',
  CONTRACT_EXPIRED: 'red',
};

const STATUS_COLOR = {
  UNREACHABLE: 'red',
  NOT_FULFILLED: 'orange',
  PLANNED: 'blue',
  FULFILLED: 'green',
  NOT_APPLICABLE: 'default',
};

// One line in the info block per column of the list.
const HELP_KEYS = [
  'fpHelpAction',
  'fpHelpActionDays',
  'fpHelpBalance',
  'fpHelpBestBlock',
  'fpHelpPending',
  'fpHelpDeadline',
  'fpHelpEntitlement',
  'fpHelpCheck',
];
const SUMMARY_ACTIONS = [
  'CONSECUTIVE',
  'USE_ALL',
  'NO_DAYS_LEFT',
  'MATERNITY',
  'CONTRACT_EXPIRED',
];
const SUMMARY_STATUSES = ['PLANNED', 'FULFILLED'];

// 'YYYY-MM-DD' -> 'DD.MM.YYYY'
const fmtDay = (s) => {
  if (!s || typeof s !== 'string') return '';
  const [y, m, d] = s.split('-');
  return d ? `${d}.${m}.${y}` : s;
};

/**
 * "First part of annual leave by 31.12." (Labour Law Art. 73) for one company:
 * who still has to use the first part, how many days, the balance, the best
 * block so far and the days waiting for approval. A tab of the leaves
 * overview, shown only for companies with the rule on.
 */
export default function FirstPartView({ company, onRowClick }) {
  const { t } = useTranslation(['translations']);
  const lo = (key, opts) =>
    t(`leavesOverview.${key}`, { ns: 'translations', ...opts });

  const [search, setSearch] = useState('');
  const [show, setShow] = useState('open');
  const [exporting, setExporting] = useState(false);

  const { data, isFetching } = useQuery(
    firstPartQueryOptions({ company, search, show })
  );
  const year = data?.year || new Date().getFullYear();
  const totals = data?.totals || {};
  const actionTotals = data?.actionTotals || {};

  const handleExport = async () => {
    setExporting(true);
    try {
      await API.employees.exportHolidayFirstPart(
        firstPartParams({ company, search, show })
      );
    } catch {
      message.error(lo('exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: lo('colEmployee'),
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 220,
    },
    {
      title: lo('colSector'),
      dataIndex: 'sector',
      key: 'sector',
      width: 180,
    },
    {
      title: lo('fpColSectorHeads'),
      dataIndex: 'sectorHeads',
      key: 'sectorHeads',
      width: 180,
    },
    {
      title: lo('fpColAction'),
      dataIndex: 'action',
      key: 'action',
      width: 280,
      render: (action, row) => {
        if (action) {
          return (
            <Tag color={ACTION_COLOR[action]}>{lo(`fpAction_${action}`)}</Tag>
          );
        }
        if (row.missedInParts) return '—';
        return (
          <Tag color={STATUS_COLOR[row.status]}>
            {lo(`fpStatus_${row.status}`)}
          </Tag>
        );
      },
    },
    {
      title: lo('fpColActionDays'),
      dataIndex: 'actionDays',
      key: 'actionDays',
      width: 110,
      align: 'center',
      render: (days) => (days ? <strong>{days}</strong> : '—'),
    },
    {
      title: lo('fpColBalance'),
      dataIndex: 'balance',
      key: 'balance',
      width: 100,
      align: 'center',
    },
    {
      title: lo('fpColBestBlock'),
      key: 'bestBlock',
      width: 230,
      render: (_, row) => {
        const block = row.qualifyingBlock || row.bestBlock;
        if (!block) return '—';
        return (
          <span>
            <strong>{block.days}</strong> ({fmtDay(block.dateFrom)} –{' '}
            {fmtDay(block.dateTo)})
          </span>
        );
      },
    },
    {
      title: lo('fpColPending'),
      dataIndex: 'pendingDays',
      key: 'pendingDays',
      width: 130,
      align: 'center',
      render: (days) =>
        days > 0 ? <Tag color="gold">{lo('fpPending', { days })}</Tag> : '—',
    },
    {
      title: lo('fpColDeadline'),
      key: 'deadline',
      width: 200,
      render: (_, row) =>
        row.deadlineReason && row.deadlineReason !== 'yearEnd'
          ? `${fmtDay(row.deadline)} (${lo(`fpDeadline_${row.deadlineReason}`)})`
          : fmtDay(row.deadline),
    },
    {
      title: lo('fpColEntitlement', { year }),
      dataIndex: 'entitlement',
      key: 'entitlement',
      width: 120,
      align: 'center',
    },
  ];

  return (
    <div className="space-y-4">
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message={lo('fpIntro', { min: 10, year })}
        description={
          <ul className="mb-0 list-disc space-y-1 pl-4 text-sm">
            {HELP_KEYS.map((key) => (
              <li key={key}>{lo(key, { year })}</li>
            ))}
          </ul>
        }
      />

      <Card size="small" className="shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-500">
                {lo('search')}
              </span>
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={lo('searchPlaceholder')}
                style={{ width: 200 }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-500">
                {lo('fpShow')}
              </span>
              <Segmented
                value={show}
                onChange={setShow}
                options={[
                  { value: 'open', label: lo('fpOpen') },
                  { value: 'all', label: lo('fpAll') },
                ]}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {SUMMARY_ACTIONS.filter((a) => actionTotals[a]).map((a) => (
              <Tag key={a} color={ACTION_COLOR[a]}>
                {lo(`fpAction_${a}`)}: <strong>{actionTotals[a]}</strong>
              </Tag>
            ))}
            {SUMMARY_STATUSES.filter((st) => totals[st]).map((st) => (
              <Tag key={st} color={STATUS_COLOR[st]}>
                {lo(`fpStatus_${st}`)}: <strong>{totals[st]}</strong>
              </Tag>
            ))}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              className="btn-primary h-8"
              loading={exporting}
              disabled={!data?.items?.length}
              onClick={handleExport}
            >
              {lo('exportExcel')}
            </Button>
          </div>
        </div>
      </Card>

      <Spin spinning={isFetching}>
        <Card size="small" className="min-w-0 overflow-hidden shadow-sm">
          <Table
            rowKey="employeeId"
            columns={columns}
            dataSource={data?.items || []}
            size="small"
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onClick: () => onRowClick?.(row),
              style: { cursor: 'pointer' },
            })}
            pagination={{ defaultPageSize: 50, showSizeChanger: true }}
          />
        </Card>
      </Spin>
    </div>
  );
}
